import React from 'react';
import {
  Bar,
  XAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
  ComposedChart,
  Line,
  YAxis
} from 'recharts';
import { AnimalDataChartTooltip, ReloadIconButton, Title, LegacyGrid as Grid } from '../../elements';
import { AnimalTrendDataType, AnimalTrendDataTypeTranslations } from '../../models/animal';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import { useTranslation } from 'react-i18next';
import { colors } from '../../utils';
import { Card, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { tokens } from '../../theme/tokens';
import { AnimalTrendDataPoint, BirthCycleOption } from '../../models/animal-trend-data';
import { AnimalSlaughterhouseData, AnimalWeight } from '../../models/animal-view';
import AnimalViewWeightsGraph from '../AnimalViewWeightsGraph/component';
import AnimalViewSlaughterhouse from '../AnimalViewSlaughterhouse/component';
import NoDataMessage from '../shared/NoDataMessage';

export interface IProps {
  animalTrendDataType: AnimalTrendDataType;
  trendData: AnimalTrendDataPoint[];
  slaughterhouseDataAverage: AnimalSlaughterhouseData | null | undefined;
  weightAverage: AnimalWeight | null | undefined;
  onAnimalTrendDataTypeChanged: (animalTrendDataType: AnimalTrendDataType) => void;
  reloadTrendData?: () => void;
  birthCycles?: BirthCycleOption[];
  selectedCycleDateOfBirth?: string | null;
  onCycleChanged?: (dateOfBirth: string | null) => void;
  isLoadingCycles?: boolean;
}

export default function TrendData(props: IProps) {
  const {
    animalTrendDataType,
    trendData,
    slaughterhouseDataAverage,
    weightAverage,
    onAnimalTrendDataTypeChanged,
    reloadTrendData,
    birthCycles,
    selectedCycleDateOfBirth,
    onCycleChanged,
    isLoadingCycles
  } = props;
  const { t } = useTranslation();

  // The selected type is owned by the parent - keeping a local copy here let the dropdown and the
  // request that actually gets sent disagree.
  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: AnimalTrendDataType) => {
    if (onAnimalTrendDataTypeChanged) {
      onAnimalTrendDataTypeChanged(newValue);
    }
  };

  return (
    <>
      <Grid container direction="row" alignItems="center" spacing={1} justifyContent="space-between">
        {/* Trend Data title */}
        <Grid item container xs={10} spacing={1}>
          <Grid item>
            <InsertChartIcon color="primary" fontSize="large" />
          </Grid>
          <Grid item>
            <Title>{t('f-animal-trend')}</Title>
          </Grid>
        </Grid>

        {/* reload icon button */}
        {reloadTrendData && (
          <Grid item xs={1} sx={{ textAlign: 'right' }}>
            <ReloadIconButton action={() => reloadTrendData()} />
          </Grid>
        )}
      </Grid>

      <Grid container>
        {/* Filters section */}
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'left' }}>
          <Grid container direction="row" spacing={2} alignItems="center">
            <Grid item xs={6} md={3} sx={{ m: 1, minWidth: 180 }}>
              <AnimalTrendTabs value={animalTrendDataType} handleTabChange={handleTabChange} />
            </Grid>
            {birthCycles && birthCycles.length > 0 && onCycleChanged && (
              <Grid item xs={6} md={3} sx={{ m: 1, minWidth: 180 }}>
                <BirthCycleSelector
                  cycles={birthCycles}
                  selectedDateOfBirth={selectedCycleDateOfBirth ?? null}
                  onChange={onCycleChanged}
                  disabled={isLoadingCycles}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        {/* Filters section */}

        {/* Trend data chart */}
        <Grid item xs={12}>
          <Card variant="outlined" style={{ padding: 32, height: 300 }}>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%">
                <ComposedChart
                  data={trendData}
                  style={{ fontFamily: tokens.fontFamily }}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <XAxis dataKey="value" padding={{ left: 30, right: 30 }} scale="band" />
                  <YAxis />
                  <RechartsTooltip content={<AnimalDataChartTooltip />} />
                  <Legend />
                  <CartesianGrid strokeDasharray="3 3" />
                  <Bar name={t('f-com-animal-count')} dataKey="animalCount" fill="#413ea0" />
                  <Line name=" " type="monotone" dataKey="animalCount" stroke={colors.barChartLine} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <Grid container justifyContent="center" alignItems="center" style={{ height: '100%' }}>
                <NoDataMessage text={t('f-no-data-to-display')} />
              </Grid>
            )}
          </Card>
        </Grid>
        {/* Trend data chart */}

        {/* Average slaughterhouse data */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Grid container spacing={1}>
            {slaughterhouseDataAverage && (
              <Grid item sm={6} xs={12}>
                <AnimalViewSlaughterhouse slaughterhouseData={slaughterhouseDataAverage} />
              </Grid>
            )}

            {weightAverage && (
              <Grid item sm={6} xs={12}>
                <AnimalViewWeightsGraph weight={weightAverage} />
              </Grid>
            )}
          </Grid>
        </Grid>
        {/* Average slaughterhouse data */}
      </Grid>
    </>
  );
}

export interface AnimalTrendTabsProps {
  value: number;
  handleTabChange: (event: React.ChangeEvent<{}>, newValue: AnimalTrendDataType) => void;
}

const compareOnlyTypes = [
  AnimalTrendDataType.MortalityDisease,
  AnimalTrendDataType.MortalityReason,
  AnimalTrendDataType.AntibioticsInjections,
  AnimalTrendDataType.AntibioticFree
];

export const AnimalTrendTabs: React.FC<any> = ({ value, handleTabChange }: AnimalTrendTabsProps) => {
  const { t } = useTranslation();

  const enumValues = Object.keys(AnimalTrendDataType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => AnimalTrendDataType[key as keyof typeof AnimalTrendDataType])
    .filter((val) => !compareOnlyTypes.includes(val));

  return (
    <FormControl variant={'outlined'} size="small" fullWidth>
      <InputLabel id="supplyLabel">{t('f-com-animal-trend-type')}</InputLabel>
      <Select
        label={t('f-com-animal-trend-type')}
        labelId="supplyLabel"
        id="supplySelect"
        value={value}
        onChange={(e) =>
          handleTabChange(e as React.ChangeEvent<{ value: unknown }>, e.target.value as AnimalTrendDataType)
        }
      >
        {enumValues.map((value, index) => (
          <MenuItem key={index} value={value}>
            {t(AnimalTrendDataTypeTranslations[value])}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export interface BirthCycleSelectorProps {
  cycles: BirthCycleOption[];
  selectedDateOfBirth: string | null;
  onChange: (dateOfBirth: string | null) => void;
  disabled?: boolean;
}

export const BirthCycleSelector: React.FC<BirthCycleSelectorProps> = ({
  cycles,
  selectedDateOfBirth,
  onChange,
  disabled = false
}) => {
  const { t } = useTranslation();

  return (
    <FormControl variant="outlined" size="small" fullWidth disabled={disabled}>
      <InputLabel id="cycleLabel">{t('f-com-birth-cycle')}</InputLabel>
      <Select
        label={t('f-com-birth-cycle')}
        labelId="cycleLabel"
        id="cycleSelect"
        value={selectedDateOfBirth ?? 'all'}
        onChange={(e) => onChange(e.target.value === 'all' ? null : (e.target.value as string))}
      >
        <MenuItem value="all">{t('f-com-all')}</MenuItem>
        {cycles.map((cycle) => (
          <MenuItem key={cycle.dateOfBirth} value={cycle.dateOfBirth}>
            #{cycle.birthCycleNumber} - {new Date(cycle.dateOfBirth).toLocaleDateString()}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
